import { pushApi } from '@/lib/api';
import { detectPushPlatform, isStandaloneDisplay } from '@/lib/pwa';

export { detectPushPlatform, isAppleTouchDevice, isStandaloneDisplay } from '@/lib/pwa';
export type { PushPlatform } from '@/lib/pwa';

const INSTALL_NUDGE_DISMISS_KEY = 'balance.push.installNudgeDismissed';

/**
 * Why Settings → Notifications can or cannot offer Enable on this device.
 * Push subscribe itself stays installed-PWA only on iOS; we still surface
 * guidance when the section would previously have been hidden.
 */
export type PushSetupStatus =
  | 'ready'
  | 'needs_home_screen'
  | 'permission_denied'
  | 'unsupported';

export function getNotificationPermission(): NotificationPermission | 'unknown' {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unknown';
  }
  return Notification.permission;
}

export function getPushSetupStatus(input?: {
  isStandalone?: boolean;
  pushSupported?: boolean;
  permission?: NotificationPermission | 'unknown';
}): PushSetupStatus {
  const standalone = input?.isStandalone ?? isStandaloneDisplay();
  const supported = input?.pushSupported ?? isPushSupported();
  const permission = input?.permission ?? getNotificationPermission();

  if (!standalone) {
    return 'needs_home_screen';
  }
  if (!supported) {
    return 'unsupported';
  }
  if (permission === 'denied') {
    return 'permission_denied';
  }
  return 'ready';
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

function keysEqual(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.byteLength) return false;
  const view = new Uint8Array(a);
  for (let i = 0; i < b.byteLength; i += 1) {
    if (view[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Convert a VAPID public key into the BufferSource PushManager.subscribe expects.
 * Throws a clear error when the server key is not an uncompressed P-256 point —
 * browsers otherwise surface a cryptic "string did not match the expected pattern".
 */
export function applicationServerKeyFromVapid(publicKey: string | null | undefined): ArrayBuffer {
  if (!publicKey?.trim()) {
    throw new Error('Server did not return a VAPID public key');
  }

  let key: Uint8Array;
  try {
    key = urlBase64ToUint8Array(publicKey.trim());
  } catch {
    throw new Error(
      'Server VAPID public key is not valid Base64. Check BALANCE_VAPID_PUBLIC_KEY on the server.'
    );
  }

  if (key.byteLength !== 65 || key[0] !== 0x04) {
    throw new Error(
      'Server VAPID public key is invalid (expected uncompressed P-256). '
        + 'Check BALANCE_VAPID_PUBLIC_KEY / BALANCE_VAPID_PRIVATE_KEY on the server.'
    );
  }

  // Copy into a plain ArrayBuffer — some browsers reject views over larger buffers.
  const copy = new Uint8Array(key.byteLength);
  copy.set(key);
  return copy.buffer;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  );
}

export function wasInstallNudgeDismissed(): boolean {
  return localStorage.getItem(INSTALL_NUDGE_DISMISS_KEY) === '1';
}

export function dismissInstallNudge(): void {
  localStorage.setItem(INSTALL_NUDGE_DISMISS_KEY, '1');
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.ready;
}

export async function enablePushNotifications(): Promise<boolean> {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return false;
  }

  const registration = await ensureServiceWorker();
  if (!registration) {
    throw new Error('Service worker is not ready');
  }

  const { publicKey } = await pushApi.getVapidPublicKey();
  const applicationServerKey = applicationServerKeyFromVapid(publicKey);
  const expectedKeyBytes = new Uint8Array(applicationServerKey);

  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !keysEqual(subscription.options.applicationServerKey, expectedKeyBytes)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (/expected pattern|InvalidAccessError|InvalidCharacterError/i.test(message)) {
        throw new Error(
          'Browser rejected the VAPID public key. '
            + 'Set valid BALANCE_VAPID_PUBLIC_KEY and BALANCE_VAPID_PRIVATE_KEY on the server '
            + '(uncompressed P-256 URL-safe Base64), then restart.'
        );
      }
      throw err;
    }
  }

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Invalid push subscription from the browser');
  }

  await pushApi.subscribe({
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
    userAgent: navigator.userAgent,
    platform: detectPushPlatform(),
  });

  await pushApi.sendTest();
  dismissInstallNudge();
  return true;
}

export async function disablePushNotifications(): Promise<void> {
  const registration = await ensureServiceWorker();
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await pushApi.unsubscribe(subscription.endpoint);
    await subscription.unsubscribe();
  }
}
